import * as Toggle from '@radix-ui/react-toggle-group'
import {
  MapFeatureType,
  useFeatureInteractionContext,
} from 'features/siteMap/contexts/FeatureInteractionContext'
import { useTilesetMapFeatureHelpers } from 'features/siteMap/tilesetMap/features/useTilesetMapFeatureHelpers'
import isNil from 'lodash/isNil'
import React from 'react'
import styled from 'styled-components'
import {
  isSitePrimitiveKind,
  SiteFeaturePrimitive,
  SiteFeaturePrimitiveKind,
} from 'types'

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const StyledToggleRoot = styled(Toggle.Root)`
  all: unset;
  display: inline-flex;
  color: ${({ theme }) => theme.forecast.text1};
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
`
const StyledToggleItem = styled(Toggle.Item)`
  all: unset;
  width: 4rem;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.forecast.text2} !important;
    background-color: ${({ theme }) => theme.forecast.background4};
  }

  &[data-state='on'] {
    color: ${({ theme }) => theme.forecast.text1} !important;
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }

  &[data-state='off'] {
    cursor: pointer;
  }
`

interface ShapeSelectorProps {
  feature: SiteFeaturePrimitive
  featureType: MapFeatureType
}

export const ShapeSelector: React.FC<ShapeSelectorProps> = (props) => {
  const { setSelectedFeature } = useFeatureInteractionContext()
  const { updateFeaturePrimitive, convertShapeDimensions } =
    useTilesetMapFeatureHelpers()

  const onChange = (value: string) => {
    if (!isSitePrimitiveKind(value) || value === props.feature.kind) return

    let updatedFeature = {
      ...props.feature,
      kind: value,
    } as SiteFeaturePrimitive // we need to do `as` here, because the value ** will not ** match the ...feature values.
    // this will be fixed in the convertShapeDimensions call

    updatedFeature = convertShapeDimensions(
      updatedFeature,
      props.feature.kind,
      value
    )

    setSelectedFeature(
      isNil(updatedFeature.unit_id)
        ? {
            type: MapFeatureType.Unknown,
            featureID: updatedFeature.id,
          }
        : {
            type: MapFeatureType.Unit,
            unitID: updatedFeature.unit_id,
          }
    )
    updateFeaturePrimitive(updatedFeature)
  }

  return (
    <StyledContainer>
      Shape:
      <StyledToggleRoot
        type='single'
        value={props.feature.kind}
        aria-label='Set Theme'
        onClick={(event) => event.stopPropagation()}
        onValueChange={(value) => onChange(value)}>
        {Object.values(SiteFeaturePrimitiveKind).map((kind) => (
          <StyledToggleItem key={kind} value={kind}>
            {kind}
          </StyledToggleItem>
        ))}
      </StyledToggleRoot>
    </StyledContainer>
  )
}
